import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import nth from "lodash/nth";

import { useBreakPoint, useDestructiveDialog, useFeatureToggle, useIntents, useModal, useQueryParams } from "@hooks";

import {
  type MeetingRoom,
  getFloorsWithMapByBuildingId,
  getUser,
  loadAllMeetingRooms,
  withAsyncThunkErrorHandling,
  getFloorById,
  getShouldShowHereAndNowConnectYourCalendarFullScreen,
  getDisplayCalendarSyncTile,
  type Area,
  getIsLoadingCalendarState,
  getIsLoadingUserCheckIn,
  getIsLoadingWorkspaceReservations,
  getIsOccupancyEnabled,
  loadWorkspaceOccupancy,
  getWorkspaceOccupancyLoadingStatusForBuildingId,
  getAvailableDeskAmenityDataByBuildingId,
  getAvailableActivityTypeDataByBuildingId,
  getAvailableDeskAmenitiesForBuilding,
  getAvailableActivityTypesForBuilding,
  AreaWithSensorInfo,
  getDefaultBuilding,
  getAllBuildings,
  getBuildingById,
  getAllMeetingRoomSuggestionsUnfiltered,
  getMapDataByFloorId,
  useAppDispatch,
  useAppSelector,
  getMeetingRoomSuggestionByBuildingId,
  getIsHMEnabledAndIntegrationEnabled,
} from "@lib/store";

import { addMinutes, parseISO, startOfMinute, differenceInMinutes, startOfToday, format, endOfToday } from "date-fns";

import { DEFAULT_NEW_EVENT_DURATION as DURATION, MINUTES_TRESHOLD_FOR_STALE_DATA } from "@constants";
import { Div, FlexCol, FlexRow, H3 } from "@quarks";
import {
  HaNMaps,
  HaNModalContainer,
  HaNFilters,
  HaNHorizontalListContent,
  HaNVerticalListContent,
  isMeetingRoom,
  HaNCalendarSyncTile,
  HaNCalendarSync,
  SingleSelectCard,
  HaNExploreTab,
  HaNExploreSearchBar,
} from "@organisms";
import { Canvas, Heading, HereAndNowLayout } from "@templates";

import {
  AreaEventTarget,
  FacilityEventTarget,
  RoomEventTarget,
} from "../../../../../../submodules/map/mapiq-map/EventTarget";
import {
  ControlsRow,
  HaNPageHeader,
  MapWrapper,
  RoomListGridArea,
  StyledIconNav,
  StyledNavLink,
  StyledNavLinkWithIcon,
  StyledTabs,
  Title,
} from "./styles";
import { IconButton, NoMapPlaceholder, SwiperRefType } from "@molecules";
import { useNavigate } from "react-router-dom";
import { Loader } from "@atoms";
import { QueryParamConstants } from "@lib/logic";
import { useExploreState } from "./useExploreState";
import { FacilityResource, Resource } from "@lib/store/src/data/buildingResources";
import { IntentType, trackEvent, trackPageComponentedRendered } from "@lib/infrastructure";
import { actions } from "@lib/store/src/data/timeslots/actions";
import { FloorSelector } from "./FloorSelector/FloorSelector";
import { HereAndNowProvider, useHereAndNow } from "./Context/HereAndNowContext";

const initialStartDateTime = startOfMinute(new Date()).toISOString();
const initialEndDateTime = addMinutes(parseISO(initialStartDateTime), 30).toISOString();

export const HaN_Previous_Tab = "HaN_Previous_Tab";

export const HereAndNow = () => {
  return (
    <HereAndNowProvider>
      <HereAndNowContent />
    </HereAndNowProvider>
  );
};

export const HereAndNowContent = () => {
  const queryParamKeys = QueryParamConstants.hereAndNow.explore;
  // * UI
  const ref = useRef<SwiperRefType | null>(null);
  const { t } = useTranslation();
  const breakPoint = useBreakPoint();
  const { addPages, openModal } = useModal();
  const { destructiveDialog } = useDestructiveDialog();

  // * URL INITIALIZATION
  const {
    q: exploreQuery,
    r,
    removeURLParams,
    i: intentRaw,
  } = useQueryParams([
    queryParamKeys.search,
    queryParamKeys.buildingId,
    queryParamKeys.resourceId,
    QueryParamConstants.intent,
  ]);
  const [preselectedObjectId] = useState(r);
  const { setIntentIfNotSet } = useIntents();

  // react router tells us to not perform navigation in a render function
  // so this needs to be in an effect
  useEffect(() => {
    setIntentIfNotSet(IntentType.parse(intentRaw));
    removeURLParams();
  }, [setIntentIfNotSet, intentRaw, removeURLParams]);

  // * FILTERS
  const [{ startDateTime, endDateTime }, setRoomDateTimes] = useState({
    startDateTime: initialStartDateTime,
    endDateTime: initialEndDateTime,
  });

  const lastOccupancyLoadForBuildingId = useRef<Record<string, Date | undefined>>({});

  // * STATE
  const [roomInformationOpen, setRoomInformationOpen] = useState(false);
  const [warnBeforeOpeningNewRoom, setWarnBeforeOpeningNewRoom] = useState(false);

  // * REDUX SELECTORS
  const dispatch = useAppDispatch();
  const allBuildings = useAppSelector(getAllBuildings);
  const { id: loggedInUserId } = useAppSelector(getUser);

  const {
    tab,
    buildingId,
    setBuildingId,
    selectedFloorId,
    setSelectedFloorId,
    filterData,
    hoverRoomId,
    selectedObject,
    setSelectedObject,
  } = useHereAndNow();

  const navigate = useNavigate();

  const minutesThresholdForStaleData = 5;
  const showCalendarConsentMessageNoOccupancy = useAppSelector(getShouldShowHereAndNowConnectYourCalendarFullScreen);
  const displayCalendarSyncTile = useAppSelector(getDisplayCalendarSyncTile);
  const { WorkspacesOnHereAndNow: isWorkspacesOnHereAndNowEnabled } = useFeatureToggle();
  const isMeetingCalendarEnabled = useAppSelector(getIsHMEnabledAndIntegrationEnabled);
  const calendarLoadingStatus = useAppSelector(getIsLoadingCalendarState);
  const isOccupancyEnabled = useAppSelector(getIsOccupancyEnabled);
  const loadingCalendar = calendarLoadingStatus === "Loading";
  const occupancyLoadingStatus = useAppSelector(state =>
    getWorkspaceOccupancyLoadingStatusForBuildingId(state, buildingId),
  );
  const isLoadingSensors = occupancyLoadingStatus === "loading" && isOccupancyEnabled;
  const { status: meetingRoomLoadingStatus } =
    useAppSelector(state => getMeetingRoomSuggestionByBuildingId(state, buildingId)) ?? {};
  const isLoadingUserCheckIn = useAppSelector(getIsLoadingUserCheckIn);
  const isLoadingWorkspaceReservations = useAppSelector(getIsLoadingWorkspaceReservations);
  const isLoadingBuilding = isLoadingUserCheckIn || isLoadingWorkspaceReservations;
  const loadingMeetingRooms = meetingRoomLoadingStatus === "loading";
  const isLoading = loadingCalendar || loadingMeetingRooms || isLoadingSensors;
  const { status: deskAmenitiesLoadingStatus } = useAppSelector(getAvailableDeskAmenityDataByBuildingId(buildingId));
  const allRoomsWithAnyScore = useAppSelector(state => getAllMeetingRoomSuggestionsUnfiltered(state, buildingId));
  const { status: activityTypesLoadingStatus } = useAppSelector(getAvailableActivityTypeDataByBuildingId(buildingId));

  const defaultBuilding = useAppSelector(getDefaultBuilding);

  const buildingName = useAppSelector(state => getBuildingById(state, buildingId))?.name || t("translation:Booked");

  // Current set-up forces us to do all these things in the parent component...
  const exploreState = useExploreState({
    buildingId,
    initialQuery: exploreQuery ?? "",
    preselectedResourceId: preselectedObjectId,
  });

  useEffect(
    function loadAvailability() {
      dispatch(
        actions.fetchWorkspaceReservationCountsForWorkday({
          workday: {
            buildingId,
            localStart: format(startOfToday(), "yyyy-MM-dd'T'HH:mm:ss"),
            localEnd: format(endOfToday(), "yyyy-MM-dd'T'HH:mm:ss"),
          },
        }),
      );
    },
    [dispatch, buildingId],
  );

  const { filteredData: rooms, selectedFilters: roomSelectedFilters } = filterData.rooms;

  const { filteredData: areas, allData: allAreas } = filterData.workspaces;

  const mapData = useAppSelector(getMapDataByFloorId(buildingId, selectedFloorId || ""));
  const floorsWithMap = useAppSelector(state => getFloorsWithMapByBuildingId(state, buildingId));
  const mapFloor = useAppSelector(state => getFloorById(state, selectedFloorId || ""));

  const [roomListVerticalOpen, setRoomListVerticalOpen] = useState(floorsWithMap.length ? false : true);
  // * CONSTS

  const duration = Number(roomSelectedFilters["SECTION_ID_DURATION"]?.[0]) || DURATION;
  const displayControlsRow = mapFloor && !showCalendarConsentMessageNoOccupancy;

  const paddingLeftPixels = breakPoint === "xLarge" ? 420 : 0; // Width of a modal
  const paddingBottomPixels = breakPoint === "small" ? 100 : 0; // Height of a horizontal rooms list
  const areFiltersTransparent = breakPoint === "small" && roomListVerticalOpen;

  const viewportRestrictions = useMemo(
    () => ({
      paddingTopPixels: 80, // Filters height and padding
      paddingRightPixels: 0,
      paddingBottomPixels: paddingBottomPixels,
      paddingLeftPixels,
    }),
    [paddingLeftPixels, paddingBottomPixels],
  );

  const objectToSelectInitially = useMemo(
    () => (tab === "workspaces" ? areas[0] ?? null : tab === "rooms" ? rooms[0] ?? null : null),
    [tab, areas, rooms],
  );

  const isInMobileView = breakPoint === "small";
  const hasInitiallySelectedObject = selectedObject !== null;

  // * FUNCS

  const loadHereAndNowRooms = useCallback(() => {
    const loadRooms = async () => {
      await dispatch(
        withAsyncThunkErrorHandling(() =>
          loadAllMeetingRooms({
            startDateTime: newStartDateTime,
            endDateTime: newEndDateTime,
            buildingId: buildingId,
            internalAttendeeIds: [loggedInUserId],
            totalAttendees: 1,
          }),
        ),
      );
    };

    // Check required feature flags
    if (!(isOccupancyEnabled || isMeetingCalendarEnabled)) return;

    const newStartDateTime = startOfMinute(new Date()).toISOString();
    const newEndDateTime = addMinutes(parseISO(newStartDateTime), duration).toISOString();

    setRoomDateTimes({ startDateTime: newStartDateTime, endDateTime: newEndDateTime });

    if (buildingId && duration && loggedInUserId) {
      loadRooms();
    }
  }, [buildingId, dispatch, duration, loggedInUserId, isOccupancyEnabled, isMeetingCalendarEnabled]);

  const fetchOccupancySensorData = useCallback(() => {
    if (!isLoadingBuilding && buildingId) {
      dispatch(withAsyncThunkErrorHandling(() => loadWorkspaceOccupancy(buildingId)));

      lastOccupancyLoadForBuildingId.current[buildingId] = startOfMinute(new Date());
    }
  }, [buildingId, dispatch, isLoadingBuilding]);

  const refreshRoomsData = () => {
    if (buildingId && loggedInUserId) {
      const now = startOfMinute(new Date());
      if (differenceInMinutes(now, parseISO(startDateTime)) >= MINUTES_TRESHOLD_FOR_STALE_DATA) {
        loadHereAndNowRooms();
      }
    }
  };

  const toggleListOrMapView = () => {
    if (!roomListVerticalOpen) {
      ref.current?.scrollToSlide(0);
    }
    setRoomListVerticalOpen(!roomListVerticalOpen);
  };

  const openObjectInformation = async (entity: MeetingRoom | AreaWithSensorInfo | Resource | undefined | null) => {
    if (!entity) {
      setSelectedObject(null);
      return;
    }

    let resource: FacilityResource | MeetingRoom | AreaWithSensorInfo | null = null;

    if (isMeetingRoom(entity) && warnBeforeOpeningNewRoom) {
      const confirmation = await destructiveDialog(
        t("meeting:DiscardEventChangesTitle"),
        t("meeting:DiscardEventChangesMessage"),
        "warning",
        t("meeting:DiscardEventChangesConfirm"),
      );
      if (!confirmation) return;
      setSelectedFloorId(entity.floorId);
      setSelectedObject(resource);
      setRoomInformationOpen(true);

      return;
    }

    switch (entity.type) {
      case "meetingRoom":
      case "room": {
        const room = allRoomsWithAnyScore.find(r => r.id === entity.id);
        if (!room) {
          setSelectedObject(null);
          return;
        }
        resource = room;
        setSelectedFloorId(room.floorId);
        break;
      }
      case "facility": {
        resource = entity as FacilityResource;
        setSelectedFloorId(entity.floorId);
        break;
      }
      case "area": {
        const area = allAreas.find(a => a.id === entity.id);
        if (!area) {
          setSelectedObject(null);
          return;
        }
        resource = area;
        setSelectedFloorId(area.floorId);
        break;
      }
    }

    setSelectedObject(resource);
    setRoomInformationOpen(true);
  };

  const closeDetailsModal = () => {
    setRoomInformationOpen(false);
    setSelectedObject(null);
    if (exploreState.selectedResource) {
      exploreState.setSelectedResource(null);
    }
  };

  const handleOnBackExplore = () => {
    if (exploreState.rawQuery.length) {
      exploreState.setRawQuery("");
      return;
    }

    const previousTab = sessionStorage.getItem(HaN_Previous_Tab) ?? "/now/rooms";

    setRoomInformationOpen(false);
    setSelectedObject(null);

    navigate(previousTab);
  };

  const resetListState = () => {
    if (breakPoint === "small") {
      ref.current?.scrollToSlide(0);
    }
  };

  const resetEntityState = useCallback(
    (tabValue?: string) => {
      if (breakPoint === "small") {
        const objectToSelect = tabValue === "rooms" ? rooms[0] : areas[0];
        setRoomInformationOpen(false);
        setSelectedObject(objectToSelect);
      } else {
        setRoomInformationOpen(false);
        setSelectedObject(null);
      }
    },
    [areas, breakPoint, rooms, setSelectedObject],
  );

  const handleRoomClick = (e: RoomEventTarget | AreaEventTarget | FacilityEventTarget) => {
    const isSmall = breakPoint === "small";

    if (tab === "explore") {
      const resourceIndex = exploreState.highlightedResources.findIndex(r => r.id === e.nodeId);
      const resource = exploreState.highlightedResources[resourceIndex];
      // in case a search term is provided but the user clicks on a resource that doesn't match the search term, we still want to show the resource information instead of doing nothing. This is a bit of an edge case but can happen and is better than showing no feedback at all.
      const fallbackResource = exploreState.allResources.find(r => r.id === e.nodeId);

      if (isSmall) {
        ref?.current?.scrollToSlide(resourceIndex);
        return;
      }

      if (resource) {
        openObjectInformation(resource);
      } else if (fallbackResource) {
        openObjectInformation(fallbackResource);
      }
      return;
    }

    if (e.type === "room" || e.roomId) {
      const roomCollection = allRoomsWithAnyScore;
      const roomIndex = roomCollection.findIndex(room => room.id === e.roomId);
      const room = roomCollection[roomIndex];

      if (isSmall) {
        ref?.current?.scrollToSlide(roomIndex);
        return;
      }

      if (room) {
        openObjectInformation(room);
      }

      return;
    }

    if (e.areaId) {
      const areaIndex = areas.findIndex(area => area.id === e.areaId);
      const area = areas[areaIndex];

      if (isSmall) {
        ref?.current?.scrollToSlide(areaIndex);
        return;
      }

      if (area) {
        openObjectInformation(area);
      }
    }
  };

  const handleOnSlideChange = (visibleObject: MeetingRoom | AreaWithSensorInfo) => {
    setSelectedFloorId(visibleObject.floorId);
    setSelectedObject(visibleObject);
  };

  const handleBookNow = () => {
    if (selectedObject !== null) {
      if (isMeetingRoom(selectedObject) && breakPoint === "small") {
        const currentIndex = rooms.indexOf(selectedObject!);
        const nextRoomValue = nth(rooms, currentIndex + 1) ?? nth(rooms, currentIndex - 1) ?? null;
        setSelectedObject(nextRoomValue);
      }
    }
  };

  const handleBuildingSelectClick = () => {
    addPages([
      <SingleSelectCard
        defaultId={defaultBuilding?.id || buildingId}
        preselectedId={buildingId}
        headerTitle={t("screen:MeetingBuildingSelection")}
        entities={allBuildings.reduce(
          (buildings, currentBuilding) => {
            if (currentBuilding.id !== defaultBuilding?.id) {
              buildings.push({
                id: currentBuilding?.id ?? "",
                label: currentBuilding?.name ?? "",
              });
            }
            return buildings;
          },
          [{ id: defaultBuilding?.id || "", label: defaultBuilding?.name || "" }],
        )}
        onSelect={(selectedBuildingId: string) => {
          trackEvent("HereAndNow__ChangeBuilding", { id: selectedBuildingId });
          setBuildingId(selectedBuildingId);
          closeDetailsModal();
        }}
      />,
    ]);
    openModal();
  };

  // * SIDEEFFECTS

  useEffect(() => {
    switch (tab) {
      case "rooms":
        trackPageComponentedRendered("hereAndNowRooms", !isLoading);
        break;
      case "workspaces":
        trackPageComponentedRendered("hereAndNowWorkspaces", !isLoading);
        break;
      case "explore":
        trackPageComponentedRendered("hereAndNowExplore", !isLoading);
        break;
    }
  }, [dispatch, isLoading, tab]);

  useEffect(() => {
    if (isLoadingBuilding || !buildingId) return;

    if (deskAmenitiesLoadingStatus === "NotLoaded") {
      dispatch(withAsyncThunkErrorHandling(() => getAvailableDeskAmenitiesForBuilding({ buildingId })));
    }

    if (activityTypesLoadingStatus === "NotLoaded") {
      dispatch(withAsyncThunkErrorHandling(() => getAvailableActivityTypesForBuilding({ buildingId })));
    }
  }, [activityTypesLoadingStatus, buildingId, deskAmenitiesLoadingStatus, dispatch, isLoadingBuilding]);

  useEffect(() => {
    const correctTab = tab === "workspaces" || tab === "rooms";
    function matchObjecAndTabType(
      objectType: MeetingRoom["type"] | Area["type"] | FacilityResource["type"],
      tabType: "workspaces" | "rooms",
    ) {
      if (
        (objectType === "area" && tabType === "workspaces") ||
        (objectType === "meetingRoom" && tabType === "rooms")
      ) {
        return true;
      }
      return false;
    }
    if (correctTab && selectedObject && !matchObjecAndTabType(selectedObject.type, tab)) resetEntityState(tab);
  }, [resetEntityState, selectedObject, tab]);

  useEffect(() => {
    // We are currently fetching sensor data even when on rooms tab,
    // looking to optimise this in the future with reduxQuery.

    if (!isOccupancyEnabled || !isWorkspacesOnHereAndNowEnabled) return;

    const now = startOfMinute(new Date());
    const lastLoadForBuilding = lastOccupancyLoadForBuildingId.current[buildingId];

    if (!lastLoadForBuilding || differenceInMinutes(now, lastLoadForBuilding) >= minutesThresholdForStaleData) {
      fetchOccupancySensorData();
    }
  }, [buildingId, isOccupancyEnabled, fetchOccupancySensorData, isWorkspacesOnHereAndNowEnabled]);

  useEffect(() => {
    setRoomListVerticalOpen(floorsWithMap.length ? false : true);
  }, [floorsWithMap]);

  useEffect(() => loadHereAndNowRooms(), [loadHereAndNowRooms]);

  useEffect(() => {
    if (isInMobileView && !hasInitiallySelectedObject) {
      // On mobile we need to pre-select a room, but not on larger screens
      setSelectedObject(objectToSelectInitially);
    }
  }, [hasInitiallySelectedObject, isInMobileView, objectToSelectInitially, setSelectedObject]);

  // * UI

  if (isLoading) {
    return (
      <Canvas centerContent>
        <Loader />
      </Canvas>
    );
  }

  return (
    <>
      <Heading>
        <HaNPageHeader
          buttonIcons={
            floorsWithMap.length
              ? [
                  {
                    icon: roomListVerticalOpen ? "map" : "weekView",
                    label: roomListVerticalOpen ? t("hereAndNow:Map") : t("hereAndNow:ListOfRooms"),
                    onClick: toggleListOrMapView,
                    showButton: "mobile",
                  },
                ]
              : undefined
          }
          title={buildingName}
          onBuildingSelect={allBuildings.length > 1 ? handleBuildingSelectClick : undefined}
        />
      </Heading>
      <HereAndNowLayout
        $displayCalendarSyncTile={displayCalendarSyncTile}
        $areWorkspacesEnabled>
        {selectedFloorId && mapData.status !== "Failed" ? (
          <MapWrapper>
            <HaNMaps
              buildingId={buildingId}
              floorId={selectedFloorId}
              hoverObjectId={hoverRoomId}
              onObjectClick={handleRoomClick}
              selectedObject={selectedObject}
              viewportRestrictions={viewportRestrictions}
              exploreState={exploreState}
              onOutsideClick={closeDetailsModal}
            />
          </MapWrapper>
        ) : (
          <MapWrapper>
            <NoMapPlaceholder />
          </MapWrapper>
        )}
        {displayCalendarSyncTile && (
          <Div
            display="none"
            sm={{ display: "grid", gridArea: "calendar-sync-tile", zIndex: 1 }}>
            <HaNCalendarSyncTile />
          </Div>
        )}
        {tab !== "explore" && (
          <Div
            gridArea="tabs"
            display="none"
            zIndex={10}
            sm={{
              display: roomListVerticalOpen ? "none" : "block",
            }}>
            <StyledTabs>
              {(isMeetingCalendarEnabled || isOccupancyEnabled) && (
                <StyledNavLink
                  onClick={() => {
                    resetEntityState("rooms");
                    refreshRoomsData();
                    sessionStorage.setItem(HaN_Previous_Tab, "/now/rooms");
                  }}
                  $isActive={tab === "rooms"}
                  to={"/now/rooms"}>
                  {t("hereAndNow:Rooms")}
                </StyledNavLink>
              )}
              {isWorkspacesOnHereAndNowEnabled && (
                <StyledNavLink
                  onClick={() => {
                    resetEntityState("workspaces");
                    sessionStorage.setItem(HaN_Previous_Tab, "/now/workspaces");
                  }}
                  $isActive={tab === "workspaces"}
                  to={"/now/workspaces"}>
                  {t("hereAndNow:Workspaces")}
                </StyledNavLink>
              )}
              <StyledNavLinkWithIcon to={"/now/explore"}>
                <StyledIconNav
                  onClick={() => resetEntityState()}
                  weight="bold"
                  icon="search"
                  size="20px"
                />
              </StyledNavLinkWithIcon>
            </StyledTabs>
          </Div>
        )}

        {tab === "explore" && !roomListVerticalOpen && (
          <FlexRow
            gridArea="tabs"
            display="none"
            sm={{ display: "flex" }}
            justifyContent="center"
            alignItems="center">
            <HaNExploreSearchBar
              exploreState={exploreState}
              onBack={handleOnBackExplore}
              showBackButton={!!(isWorkspacesOnHereAndNowEnabled || isMeetingCalendarEnabled)}
            />
          </FlexRow>
        )}

        {!showCalendarConsentMessageNoOccupancy ? (
          <Div
            gridArea="filter"
            zIndex={1}
            overflowX={"hidden"}
            className="min-h-11">
            <HaNFilters
              swiperFilterReset={resetListState}
              areFilterTransparent={areFiltersTransparent}
            />
          </Div>
        ) : null}

        {displayControlsRow ? (
          <ControlsRow
            alignItems="center"
            gridArea="controls"
            justifyContent="flex-end"
            gap={8}
            zIndex={1}>
            <FloorSelector />
          </ControlsRow>
        ) : null}
        <Div
          gridArea={
            showCalendarConsentMessageNoOccupancy
              ? "controls / room-list-horizontal / room-list-horizontal / room-list-horizontal"
              : "room-list-horizontal"
          }
          display="none"
          sm={{ display: "grid" }}>
          <HaNHorizontalListContent
            buildingId={buildingId}
            showCalendarConsentMessageNoOccupancy={showCalendarConsentMessageNoOccupancy}
            endDateTime={endDateTime}
            onClick={openObjectInformation}
            onSlideChange={handleOnSlideChange}
            startDateTime={startDateTime}
            exploreState={exploreState}
            ref={ref}
          />
        </Div>
        <RoomListGridArea
          gridArea="room-list"
          overflow="hidden"
          zIndex={2}
          height="calc(var(--100vh, 100vh) - 60px)"
          $roomListVerticalOpen={roomListVerticalOpen}>
          <Div
            position="relative"
            top="0"
            zIndex={1}
            left="0">
            {displayCalendarSyncTile && (
              <Div sm={{ display: "grid", gridArea: "calendar-sync-tile", zIndex: 1 }}>
                <HaNCalendarSyncTile />
              </Div>
            )}
            <Div
              sm={{ display: "none" }}
              zIndex={10}
              gridArea="title">
              <Title>
                <H3 as="h1">{buildingName}</H3>
                {allBuildings.length > 1 ? (
                  <IconButton
                    aria-label="building-select"
                    icon="caretDown"
                    iconButton="tertiary"
                    onClick={handleBuildingSelectClick}
                    size="small"
                  />
                ) : null}
              </Title>
            </Div>
            {tab !== "explore" ? (
              <StyledTabs
                gridArea="tabs"
                zIndex={10}>
                {isMeetingCalendarEnabled || isWorkspacesOnHereAndNowEnabled ? (
                  <StyledNavLink
                    $isActive={tab === "rooms"}
                    onClick={() => {
                      resetEntityState("rooms");
                      refreshRoomsData();
                      sessionStorage.setItem(HaN_Previous_Tab, "/now/rooms");
                    }}
                    to={"/now/rooms"}>
                    {t("hereAndNow:Rooms")}
                  </StyledNavLink>
                ) : null}
                {isWorkspacesOnHereAndNowEnabled ? (
                  <StyledNavLink
                    $isActive={tab === "workspaces"}
                    onClick={() => {
                      resetEntityState("workspaces");
                      sessionStorage.setItem(HaN_Previous_Tab, "/now/workspaces");
                    }}
                    to={"/now/workspaces"}>
                    {t("hereAndNow:Workspaces")}
                  </StyledNavLink>
                ) : null}
                <StyledNavLinkWithIcon to={"/now/explore"}>
                  <StyledIconNav
                    onClick={() => resetEntityState()}
                    weight="bold"
                    icon="search"
                    size="20px"
                  />
                </StyledNavLinkWithIcon>
              </StyledTabs>
            ) : null}
          </Div>

          {tab === "explore" ? (
            <HaNExploreTab
              exploreState={exploreState}
              onClick={openObjectInformation}
              handleOnBackExplore={handleOnBackExplore}
            />
          ) : (
            <>
              <Div
                display="none"
                sm={{ display: "block", padding: "16px 20px" }}>
                <HaNFilters
                  swiperFilterReset={resetListState}
                  areFilterTransparent={areFiltersTransparent}
                />
              </Div>
              <FlexCol
                sm={{ display: !roomListVerticalOpen ? "none" : "flex" }}
                overflow="hidden"
                flex="auto"
                zIndex={2}>
                {showCalendarConsentMessageNoOccupancy && tab === "rooms" ? (
                  <HaNCalendarSync />
                ) : (
                  <HaNVerticalListContent
                    endDateTime={endDateTime}
                    onClick={openObjectInformation}
                    exploreState={exploreState}
                    selectedObject={selectedObject}
                    startDateTime={startDateTime}
                  />
                )}
              </FlexCol>
            </>
          )}
        </RoomListGridArea>
      </HereAndNowLayout>

      <HaNModalContainer
        setWarnBeforeOpeningNewRoom={setWarnBeforeOpeningNewRoom}
        onClose={closeDetailsModal}
        entity={selectedObject}
        show={roomInformationOpen}
        startDateTime={startDateTime}
        endDateTime={endDateTime}
        duration={duration}
        onBookNowCallback={handleBookNow}
      />
    </>
  );
};
